import { useContext, useEffect, useMemo, useState } from 'react';

import {
  CtPlatform,
  CtPlatformNetworkResponse,
  CtPlatformTypeNetworkResponse,
  CtPlatformTypeResponse,
  GroupedPlatforms,
} from 'models/general';
import { StoreContext } from 'models/import';
import Btn, { BtnModifier } from 'components/btn';
import Typeahead, { TypeaheadModifier } from 'components/form/typeahead';
import Header, { HeaderModifier } from 'components/header';
import AnchorTabs, { AnchorTabType } from 'components/anchorTabs';
import Actions, { ActionModifier } from 'components/actions';
import { getLaminasApiData } from 'services/api';
import Modal from 'components/modal';
import { useTranslation } from 'react-i18next';
import ImportSteps from 'pages/import/shared/steps';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TextfieldModifier } from 'components/form/textfield';
import ArrowLeftIcon from 'assets/icons/arrowLeft.svg';
import PlatformCards, { PlatformCardItem } from 'components/platformCards';
import MappingIcon from 'assets/icons/mapping.svg';
import ManualEntryIcon from 'assets/icons/manualentry.svg';
import ExcelIcon from 'assets/icons/excel.svg';
import CsvIcon from 'assets/icons/csv.svg';
import { TableDataRes } from 'components/table/base/tableBaseApi';
import { OverviewCsvTableDef } from 'components/table/overviewCsvTable';
import { OverviewRightTableDef } from 'components/table/overviewRightTable';

enum LocalStorage {
  HAS_ANY_TRANSACTION = 'has_any_transaction',
  SELECTED_CHECKBOX = 'import_button_selected',
}

function ImportPlatformsComponent() {
  const navigate = useNavigate();
  const { setStep, isIntegrationsPage, userSettings } = useContext(StoreContext);
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const hasAnyTransaction = !window.location.href.includes('welcome');
  // const [hasAnyTransaction, setHasAnyTransaction] = useState(true);
  const selectedCheckbox = localStorage.getItem(LocalStorage.SELECTED_CHECKBOX) || "ALL";
  //all
  const [groupedPlatforms, setGroupedPlatforms] = useState([] as GroupedPlatforms[]);
  const hasRecentlyAddedApi = useMemo(() => {
   const items = groupedPlatforms.filter((group: GroupedPlatforms) => group.label === t('Recently Used API', { ns: 'imports' }));
    if (items.length > 0) {
      return items[0].items.length > 0;
    }
    return false;
  }, [groupedPlatforms]);
  const hasRecentlyAddedCsv = useMemo(() => {
    const items = groupedPlatforms.filter((group: GroupedPlatforms) => group.label === t('Recently Used CSV', { ns: 'imports' }));
    if (items.length > 0) {
      return items[0].items.length > 0;
    }
    return false;
  }, [groupedPlatforms]);
  //by search bar
  const [filteredPlatforms, setFilteredPlatforms] = useState([] as GroupedPlatforms[]);
  //by checkbox
  const [selectedPlatforms, setSelectedPlatforms] = useState([] as GroupedPlatforms[]);

  const [searchedTerm, setSearchedTerm] = useState('');

  const setLocalStorage = (
    e: string,
  ): void => {
    try {
    localStorage.setItem("import_button_selected", e);
    } catch {}
  };
  const getCheckboxModifiers = (
    e: string,
  ): BtnModifier[] => {
    if (selectedCheckbox === e) {
      //TODO: Update border
     return [BtnModifier.INNER, BtnModifier.OLD, BtnModifier.OUTLINED_BACKGROUND, BtnModifier.IMPORTS];//, BtnModifier.BORDER];
    } else {
      return [BtnModifier.INNER, BtnModifier.OLD, BtnModifier.OUTLINED_BACKGROUND, BtnModifier.IMPORTS];
    }
  };

  const getGroupedPlatforms = (
    _platforms: CtPlatform[],
    _platformTypes: CtPlatformTypeResponse[],
  ): GroupedPlatforms[] => _platformTypes.map((type: CtPlatformTypeResponse) => {
    const platforms = isIntegrationsPage
      ? _platforms.filter((p: CtPlatform) => p.custom_api_uri || p.custom_csv_uri)
      : _platforms;

    const platformsWithBSCMapped = platforms.map((p: CtPlatform) => {
      const binanceSmartChain = p.display_name.toLowerCase().includes('binance smart chain')
      return { 
        ...p, 
        //we also want to use the display_name for search and not the name
        name: binanceSmartChain ? 'BSC Binance Smart Chain' : p.display_name,
      };
    });
    const items = platformsWithBSCMapped.filter((p: CtPlatform) => p.platform_type === type.name);
    const legacyItems = platformsWithBSCMapped.filter((p: CtPlatform) => p.legacy);

    // Legacy items need to be group seperately since there is no "platform_type" for them
    let groupedItems: GroupedPlatforms;
    if (type.name === 'legacy') {
      groupedItems = { label: type.display_name, items: legacyItems };
    } else {
      groupedItems = {
        label: type.display_name,
        items: items.filter((p: CtPlatform) => !p.legacy),
      };
    }

    return groupedItems;
  });

  const getRecentlyAdded = (
    csvItems: OverviewCsvTableDef[],
    apiItems: OverviewRightTableDef[],
  ): GroupedPlatforms[] => {

    let csvItemsMapped: CtPlatform[] = csvItems.map((item: OverviewCsvTableDef) => {
      const errors = (item.jobs || []).filter((job: any) => job.errorCode > 0).length;
      const binanceSmartChain = item.displayName.toLowerCase().includes('binance smart chain');
      return {
          platform_type: 'recently_added',
          // name: item.customName,
          name: binanceSmartChain ? 'BSC Binance Smart Chain' : item.displayName,
          display_name: item.displayName,
          description: undefined,
          import_instructions: undefined,
          logo_uri: item.logoUri || undefined,
          supported_import_types: undefined,
          recommended_import_type: undefined,
          uri: undefined,
          custom_api_uri: null,
          custom_csv_uri: item.customApiUri,
          legacy: false,
          warningCount: errors
      };
    });

    let apiItemsMapped: CtPlatform[] = apiItems.map((item: OverviewRightTableDef) => {
      const errors = (item.jobs || []).filter((job: any) => job.errorCode > 0).length;
      const binanceSmartChain = item.displayName.toLowerCase().includes('binance smart chain');
      return {
          platform_type: 'recently_added',
          name: binanceSmartChain ? 'BSC Binance Smart Chain' : item.displayName,
          display_name: item.displayName,
          description: undefined,
          import_instructions: undefined,
          logo_uri: item.logoUri || undefined,
          supported_import_types: undefined,
          recommended_import_type: undefined,
          uri: undefined,
          custom_api_uri: item.customApiUri,
          custom_csv_uri: null,
          legacy: false,
          warningCount: errors
      };
    });

    csvItemsMapped = csvItemsMapped.sort((a, b) => { return b.display_name > a.display_name ? 1 : -1; });
    apiItemsMapped = apiItemsMapped.sort((a, b) => { return b.display_name > a.display_name ? 1 : -1; });

    return [
      {
        label: t('Recently Used API', { ns: 'imports' }),
        items: apiItemsMapped,
      },
      {
      label: t('Recently Used CSV', { ns: 'imports' }),
      items: csvItemsMapped,
    }];
  };

  const getPlatformTypes = () => getLaminasApiData<CtPlatformTypeNetworkResponse>('/platform/types');
  const getPlatforms = () => getLaminasApiData<CtPlatformNetworkResponse>('/platform/list');
  const getCsvImports = () => getLaminasApiData<TableDataRes<OverviewCsvTableDef, any>>('/tx-overview-v2/import-csv-overview');
  const getApiImports = () => getLaminasApiData<TableDataRes<OverviewRightTableDef, any>>('/tx-overview-v2/import-job-overview?filter[ignoreSmallMismatches]=1');
  const getUserTransactions = () => getLaminasApiData<TableDataRes<OverviewRightTableDef, any>>('/transactions?page=0&size=1&types=&accounts=&coins=&sortDirection=desc');

  useEffect(() => {
    Promise.all([getPlatformTypes(), getPlatforms(), getCsvImports(), getApiImports()])
    // Promise.all([getPlatformTypes(), getPlatforms(), getCsvImports(), getApiImports(), getUserTransactions()])
      .then((res: [CtPlatformTypeNetworkResponse, CtPlatformNetworkResponse, TableDataRes<OverviewCsvTableDef, any>, TableDataRes<OverviewRightTableDef, any>]) => {
        if (!isIntegrationsPage) {
          setStep(0);
        }

        // const [platformTypesRes, platformsRes, csvImports, apiImports, userTransactions] = res;
        const [platformTypesRes, platformsRes, csvImports, apiImports] = res;
        const platformTypes = platformTypesRes.data;
        // TODO: We should be able to sort/add sections them from backend panel

        // if (userTransactions) {
        //   if (userTransactions.data.length > 0) {
        //     setHasAnyTransaction(false);
        //   } else {
        //     setHasAnyTransaction(true);
        //   }
        // }

        // We need to fix sorting since we get wrong sorting from backend
        platformTypes.sort((a, b) => {
          if (a.name === 'exchange') { return -1; }
          if (b.name === 'legacy') { return -1; }
          return 0;
        });
        // We need to fix custom section since we do not get it from backend
        const platforms = platformsRes.data;

        const csvImportsData = csvImports.data;
        const apiImportsData = apiImports.data;

        if (platforms && platformTypes) {
          const groupedCtPlatforms = getGroupedPlatforms(platforms, platformTypes);
          const groupedRecentlyAddedImports = getRecentlyAdded(csvImportsData, apiImportsData);

          const allItems = groupedCtPlatforms.concat(groupedRecentlyAddedImports);

          setGroupedPlatforms(allItems);
          setFilteredPlatforms(allItems);
          setSelectedPlatforms(allItems);
        }

        // localStorage.setItem(hasAnyTransactionKey, 'true');
        // localStorage.removeItem(hasAnyTransactionKey);
      }, () => { });
  }, []);

  const customGroupItems = [{
    icon: <MappingIcon />,
    name: `${t('Custom', { ns: 'imports' })}`,
    platform_type: 'custom',
    display_name: `${t('Custom', { ns: 'imports' })}`,
    custom_api_uri: 'https://cointracking.info/import/custom/',
    custom_csv_uri: null,
    beta: false,
  }, {
    icon: <ExcelIcon />,
    name: `${t('Excel', { ns: 'imports' })}`,
    platform_type: 'custom',
    display_name: `${t('Excel', { ns: 'imports' })}`,
    custom_api_uri: 'https://cointracking.info/import/import_xls/',
    custom_csv_uri: null,
    beta: false,
  }, {
    icon: <CsvIcon />,
    name: `${t('CSV', { ns: 'imports' })}`,
    platform_type: 'custom',
    display_name: `${t('CSV', { ns: 'imports' })}`,
    custom_api_uri: null,
    custom_csv_uri: 'https://cointracking.info/import/import_csv/',
    beta: false,
  }, {
    icon: <ManualEntryIcon />,
    name: `${t('Manual Entry', { ns: 'imports' })}`,
    platform_type: 'custom',
    display_name: `${t('Manual Entry', { ns: 'imports' })}`,
    custom_api_uri: 'https://cointracking.info/enter_coins.php',
    custom_csv_uri: null,
    beta: false,
  }];

  return (
    <div className="ct_padding">
      <div className={`ct_section ${isIntegrationsPage ? '_oldView' : '_import'}`}>
        <div className="ct_section-inner">
          <div className={`ct_section-main ${isIntegrationsPage ? '' : '_leftPadded'}`}>
          <div className="ct_importHeader">
                <div className="ct_importHeader-main">
                <Header
                  headline={hasAnyTransaction ? t('Add Import', { ns: 'imports' }) : t('Welcome! Time for Your First Import', { ns: 'imports' })}
                  desc2={hasAnyTransaction ? undefined : t('Start by selecting or searching for your exchange or wallet to import your transaction history.', { ns: 'imports' }) }
                  modifiers={[HeaderModifier.OLD, HeaderModifier.ONLY_TITLE_MARGIN]}
                />
                </div>
              </div>

            <div className="ct_importHeader _search">
              <div className="ct_importHeader-main">

              {/* For /import route  */}
              <div className="ct_importHeader-search">
                <Typeahead
                  filterByKey="name"
                  showDropdown={false}
                  suggestions={groupedPlatforms}
                  placeholder={t('Type your exchange, wallet or blockchain...', { ns: 'imports' })}
                  hasButton
                  hasFocus
                  debounceMs={0}
                  modifiers={[TypeaheadModifier.INTEGRATION]}
                  textfieldMods={[TextfieldModifier.INTEGRATION]}
                  buttonMods={BtnModifier.INTEGRATION}
                  cb={(data) => {
                    setFilteredPlatforms(data as unknown as GroupedPlatforms[]);
                    setSelectedPlatforms(data as unknown as GroupedPlatforms[]);
                  }}
                  searchCb={(searched) => { setShowModal(false); setSearchedTerm(searched); }}
                />
                <div className="ct_importHeader-or">
                  <p className="ct_header-text ct_body2">
                    |
                  </p>
                </div>
                <Btn onClick={() => { setSearchedTerm(''); setShowModal((current) => !current); }} type="primary" text={t('Custom Import', { ns: 'imports' })} modifier={[BtnModifier.INNER, BtnModifier.OLD, BtnModifier.OUTLINED_BACKGROUND, BtnModifier.IMPORTS]} />
              </div>
              <div className="ct_importHeader-searchButtons">
                 <Btn onClick={() => {
                  setLocalStorage("ALL");
                  setShowModal(false);
                  setSelectedPlatforms(filteredPlatforms);
                }} type="primary" text={t('All', { ns: 'imports' })} modifier={getCheckboxModifiers("ALL")} />
                <Btn onClick={() => {
                  const key = "imports_exchange_container";
                  setLocalStorage(key);
                  setShowModal(false);
                  const filtered = filteredPlatforms.filter((group: GroupedPlatforms) => group.label === 'Exchange');//For some reason the label is not translated
                  setSelectedPlatforms(filtered);
                  if (filtered.length === 0) {
                    setShowModal(true);
                  }
                }} type="primary" text={t('Exchange', { ns: 'imports' })} modifier={getCheckboxModifiers("imports_exchange_container")} />
                <Btn onClick={() => {
                  const key = "imports_blockchain_container";
                  setLocalStorage(key);
                  setShowModal(false);
                  const filtered = filteredPlatforms.filter((group: GroupedPlatforms) => group.label === 'Blockchain');//For some reason the label is not translated
                  setSelectedPlatforms(filtered);
                  if (filtered.length === 0) {
                    setShowModal(true);
                  }
                }} type="primary" text={t('Blockchain', { ns: 'imports' })} modifier={getCheckboxModifiers("imports_blockchain_container")} />
                <Btn onClick={() => {
                  const key = "imports_wallet_container";
                  setLocalStorage(key);
                  setShowModal(false);
                  const filtered = filteredPlatforms.filter((group: GroupedPlatforms) => group.label === 'Wallet');//For some reason the label is not translated
                  setSelectedPlatforms(filtered);
                  if (filtered.length === 0) {
                    setShowModal(true);
                  }
                }} type="primary" text={t('Wallet', { ns: 'imports' })} modifier={getCheckboxModifiers("imports_wallet_container")} />
                {!!(hasRecentlyAddedApi || hasRecentlyAddedCsv) && (
                <Btn onClick={() => {
                  const key = "imports_recently_added_container";
                  setLocalStorage(key);
                  setShowModal(false);
                  const filtered = filteredPlatforms.filter((group: GroupedPlatforms) => 
                      group.label === t('Recently Used API', { ns: 'imports' })
                      || group.label === t('Recently Used CSV', { ns: 'imports' })
                    );
                  setSelectedPlatforms(filtered);
                  if (filtered.length === 0) {
                    setShowModal(true);
                  }
                }} type="primary" text={t('Recently Used', { ns: 'imports' })} modifier={getCheckboxModifiers("imports_recently_added_container")} />
              )}
              </div>

              {/* For /import_new route  */}
                {!isIntegrationsPage && groupedPlatforms && !!groupedPlatforms.length && (
                <Actions modifiers={[ActionModifier.STRETCH_LEFT, ActionModifier.RIGHT]}>
                  <Typeahead
                    filterByKey="name"
                    showDropdown={false}
                    suggestions={groupedPlatforms}
                    placeholder={t('Type your exchange, wallet or blockchain...', { ns: 'imports' })}
                    hasButton
                    hasFocus={false}
                    modifiers={[]}
                    debounceMs={0}
                    textfieldMods={[TextfieldModifier.INTEGRATION]}
                    buttonMods={BtnModifier.INTEGRATION}
                    cb={(data) => setFilteredPlatforms(data as unknown as GroupedPlatforms[])}
                    searchCb={(searched) => { setShowModal(false); setSearchedTerm(searched); }}
                  />
                  <Btn onClick={() => { setShowModal(true); }} type="primary" text={t('Manual import', { ns: 'imports' })} modifier={[BtnModifier.INNER]} />
                </Actions>
                )}

              </div>
              {/* For /import route  */}
              {!!isIntegrationsPage && (
                <div className="ct_importHeader-aside">
                  <Btn onClick={() => navigate('/balancecheck')} type="primary" text={t('Balance Check', { ns: 'imports' })} modifier={[BtnModifier.OLD]} />
                </div>
              )}
            </div>
            {!isIntegrationsPage && filteredPlatforms && !!filteredPlatforms.length && (
              <AnchorTabs
                showHeader={false}
                groups={filteredPlatforms}
                hidesEmptySections
                type={AnchorTabType.IMPORT}
              />
            )}

          </div>
          {!isIntegrationsPage && (
            <div className="ct_section-aside">
              <ImportSteps />
            </div>
          )}
        </div>
      </div>
      {/* For /import route  */}
      {!!isIntegrationsPage && selectedPlatforms && !!selectedPlatforms.length && (
        <div className="ct_section _oldView">
          <div className="ct_section-inner">
            <div className="ct_importHeader-anchor _old">

              {!showModal && (
              <AnchorTabs
                showHeader={false}
                groups={selectedPlatforms}
                hidesEmptySections
                type={AnchorTabType.IMPORT}
              />
              )}
              {showModal && (
                <>
                {searchedTerm.length > 0 && (
                  <div className='ct_anchorTabs-title _import'>{t('No import found, please use one of the following options:', { ns: 'imports' })}</div>
                )}
                {searchedTerm.length === 0 && (
                  <div className='ct_anchorTabs-title _import'>{t('Custom Import', { ns: 'imports' })}</div>
                )}
                  <PlatformCards items={customGroupItems as PlatformCardItem[]} />
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {!isIntegrationsPage && (
      <Modal
        showModal={showModal}
        title={t('Manual import', { ns: 'imports' })}
        headline={t('Select one of the following methods to import or enter your data manually:', { ns: 'imports' })}
        onModalClose={() => setShowModal(false)}
        onModalAction={() => setShowModal(false)}
        isLoading={false}
      >
        <>
          <div className="ct_anchorTabs-title">{t('Custom Import', { ns: 'imports' })}</div>
          <PlatformCards items={customGroupItems as PlatformCardItem[]} />
        </>
      </Modal>
      )}
    </div>

  );
}

export default ImportPlatformsComponent;
